import React, { useState, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import map from 'lodash/map'
import filter from 'lodash/filter'
import classnames from 'classnames'
import useClickOutsideListener from '@Utils/UseClickOutsideListener'
import * as Page from '@Utils/BrowserDimensions'

import {
  IProjectVersionSettingsReviewParameterOption,
} from '@SiteModules/ProjectVersionSettings/types'

import './ReviewParameter.scss'

const DROPDOWN_OFFSET = 7 // px
const DROPDOWN_MAX_HEIGHT = 335 // px, from ReviewParameter.scss

interface ReviewParameterProps {
  readonly reviewModeOptions: string[]
  readonly availableOptions: IProjectVersionSettingsReviewParameterOption[]
  readonly buttonText: string
  readonly dropdownDirection?: 'auto' | 'up'
  /** Prefix for checkbox ids so multiple ReviewParameter-style controls on one screen stay unique. */
  readonly optionInputIdPrefix?: string
  /** Matches advanced ButtonParameter inactive styling (--text-subtle) when nothing is selected. */
  readonly buttonAdvancedInactive?: boolean
  setReviewModeOptions(newReviewModeOptions: string[]): void
}

const ReviewParameter: React.FC<ReviewParameterProps> = props => {
  const {
    reviewModeOptions,
    availableOptions,
    buttonText,
    dropdownDirection = 'auto',
    optionInputIdPrefix = 'review',
    buttonAdvancedInactive = false,
  } = props

  const [showDropdown, setShowDropdown] = useState(false)

  useEffect(() => {
    const container = document.getElementsByClassName('SetupProjectPopup')
    if (container && container.length > 0) {
      const containerElement = container[0] as HTMLElement
      containerElement.style.overflowY = showDropdown ? 'hidden' : 'scroll'
    }
  }, [showDropdown])

  useClickOutsideListener('ReviewParameter__dropdown-element', setShowDropdown, showDropdown)

  const buttonRef = useRef<HTMLDivElement | null>(null)

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown)
  }

  const stopPropagation = (e: React.MouseEvent<HTMLLabelElement>) => {
    e.stopPropagation()
  }

  const parameterOptions = map(availableOptions, (option, index) => {
    const isChecked = reviewModeOptions.includes(option.name)

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (option.disabled) { return }

      const checked = e.target.checked
      if (checked && !reviewModeOptions.includes(option.name)) {
        props.setReviewModeOptions(reviewModeOptions.concat(option.name))
      } else if (!checked && reviewModeOptions.includes(option.name)) {
        props.setReviewModeOptions(filter(reviewModeOptions, reviewOption => reviewOption !== option.name))
      }
    }

    const inputId = `${optionInputIdPrefix}-option-${index}`

    return (
      <li
        key={index}
        className={classnames(
          'ReviewParameter__dropdown-option',
          { 'ReviewParameter__dropdown-option-enabled': !option.disabled },
          { 'ReviewParameter__dropdown-option-disabled': option.disabled }
        )}
      >
        <label
          htmlFor={inputId}
          onClick={stopPropagation}
        >
          <input
            type="checkbox"
            id={inputId}
            checked={isChecked}
            onChange={handleChange}
          />
          <div className="ReviewParameter__dropdown-option-checkmark-wrapper">
            <div className="ReviewParameter__dropdown-option-checkmark"/>
          </div>
          <div className="ReviewParameter__dropdown-option-name">
            {option.nameToShowInDropdown}
          </div>
          <div
            className="ReviewParameter__dropdown-option-description"
          >
            {option.description}
          </div>
          {option.disabled && option.disabledHint && (
            <div className="ReviewParameter__dropdown-option-disabled-hint">
              {option.disabledHint}
            </div>
          )}
        </label>
      </li>
    )
  })

  const renderDropdown = () => {
    const buttonBounds = buttonRef.current ? buttonRef.current.getBoundingClientRect() : { x: 0, y: 0, width: 0, height: 0 }

    const dropdownX = buttonBounds.x
    const dropdownYBelow = buttonBounds.y + buttonBounds.height + DROPDOWN_OFFSET
    const shouldRenderDropdownAbove = dropdownDirection === 'up' || (
      dropdownYBelow + DROPDOWN_MAX_HEIGHT > Page.getHeight() &&
      buttonBounds.y - DROPDOWN_MAX_HEIGHT - DROPDOWN_OFFSET >= 0
    )
    const style = shouldRenderDropdownAbove
      ? {
          top: `${Math.max(DROPDOWN_OFFSET, buttonBounds.y - DROPDOWN_OFFSET)}px`,
          left: `${dropdownX}px`,
          transform: 'translateY(-100%)',
        }
      : {
          top: `${dropdownYBelow}px`,
          left: `${dropdownX}px`,
        }

    return ReactDOM.createPortal(
      (
        <div
          className="ReviewParameter__dropdown ReviewParameter__dropdown-element"
          style={style}
        >
          <ul className="ReviewParameter__dropdown-options">
            {parameterOptions}
          </ul>
        </div>
      ),
      document.getElementById('site-container')!
    )
  }

  return (
    <div
      className="ReviewParameter ReviewParameter__dropdown-element"
    >
      <div
        className={classnames(
          'ReviewParameter__button',
          { 'ReviewParameter__button-dropdown-open': showDropdown },
          { 'ReviewParameter__button-advanced-inactive': buttonAdvancedInactive }
        )}
        onClick={toggleDropdown}
        ref={buttonRef}
      >
        <div className="ReviewParameter__button-text">
          {buttonText}
        </div>
      </div>
      {showDropdown && renderDropdown()}
    </div>
  )
}

export default ReviewParameter
